import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import classNames from 'classnames';
import { useCallback, useEffect, useRef } from 'react';

import {
  isSelfSignupUser as isSelfSignupUserAccount,
  useGetLoggedInUser,
} from '@revelio/auth';
import { TourClasses } from '@revelio/core';
import { TaxonomyType } from '@revelio/data-access';
import { useChartTheme } from '@revelio/replots';
import { FilterTag, IconButton, Popover, useDisclosure } from '@revelio/ui';

import { useActivityTaxonomyConfig } from '../../../../shared/selection-lists/hooks/useActivityTaxonomyConfig';
import { useCurrentReferenceTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useReferenceTaxonomyVersions';
import { useCurrentRoleTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useRoleTaxonomyAdapter';
import { useTabContentConfig } from '../../components/PageConfigProvider/PageConfigProvider';
import type { PrimaryFilterConfig } from '../../components/PageConfigProvider/page-reducer';
import type { PositionedItem } from '../../components/PageConfigProvider/types/item';
import previewStyles from '../../components/SavedViews/saved-view-preview.module.css';
import { useSavedViewPreview } from '../../components/SavedViews/savedViewPreviewStore';
import { createDefaultFilterMap } from '../contexts/defaultFilters';
import { useFilterContext } from '../contexts/FilterProvider';
import { usePrimaryFilterState } from '../hooks/usePrimaryFilterState';
import { useScrollButtons } from '../hooks/useScrollButtons';
import type { PrimaryFilterId } from '../types/filters';
import { focusOnOpen, keepOpenForPortaledChildren } from './FilterPopover';
import { PrimaryFilterFormRender } from './filterForms/PrimaryFilterFormRender';
import { PrimaryFilterTag } from './PrimaryFilterTag';
import addStyles from './primary-filter-add.module.css';
import overflowStyles from './primary-filter-overflow.module.css';
import { ScrollButton } from './ScrollButton';

export const PrimaryFilter = ({
  primaryFilter,
}: {
  primaryFilter: PrimaryFilterConfig;
}) => {
  const { items, revelioPageId, dispatch } = useTabContentConfig();
  const { dispatch: filterDispatch, defaultSelectionOverrides } =
    useFilterContext();

  const areFilterChipsColored =
    !revelioPageId && getAreFilterChipsColored(items);

  const {
    primaryFilter: primaryTreeFilter,
    removePrimaryFilter,
    reorderPrimarySelections,
  } = usePrimaryFilterState(primaryFilter.id);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } })
  );

  const hasResetRef = useRef(false);
  const previousFilterIdRef = useRef(primaryFilter.id);
  const { version: roleTaxonomyVersion } = useCurrentRoleTaxonomyVersion();
  const { version: skillTaxonomyVersion } = useCurrentReferenceTaxonomyVersion({
    taxonomyType: TaxonomyType.Skill,
  });
  const activityTaxonomyConfig = useActivityTaxonomyConfig();

  const resetPrimaryFilter = useCallback(
    (filterId: PrimaryFilterId) => {
      const filterMap = createDefaultFilterMap(
        filterId,
        {
          roleTaxonomyVersion,
          skillTaxonomyVersion,
          ...activityTaxonomyConfig,
        },
        defaultSelectionOverrides
      );
      filterDispatch({
        type: 'REPLACE_FILTERS',
        payload: {
          filterMap,
          consistencyKey: filterId,
        },
      });

      dispatch({ type: 'UPDATE_PRIMARY_FILTER', payload: filterId });
    },
    [
      filterDispatch,
      dispatch,
      roleTaxonomyVersion,
      skillTaxonomyVersion,
      activityTaxonomyConfig,
      defaultSelectionOverrides,
    ]
  );

  useEffect(() => {
    // Reset the flag when the primary filter changes
    if (previousFilterIdRef.current !== primaryFilter.id) {
      hasResetRef.current = false;
      previousFilterIdRef.current = primaryFilter.id;
    }

    if (
      !hasResetRef.current &&
      primaryTreeFilter &&
      (primaryTreeFilter.primarySelections?.length ?? 0) === 0
    ) {
      hasResetRef.current = true;
      resetPrimaryFilter(primaryFilter.id);
    }
  }, [primaryTreeFilter, primaryFilter.id, resetPrimaryFilter]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (
      !over ||
      active.id === over.id ||
      !primaryTreeFilter?.primarySelections
    ) {
      return;
    }

    const startingIndex = primaryTreeFilter?.primarySelections?.findIndex(
      (filter) => filter.id === active.id
    );
    const endingIndex = primaryTreeFilter?.primarySelections?.findIndex(
      (filter) => filter.id === over.id
    );
    const newFilterOrder = arrayMove(
      primaryTreeFilter?.primarySelections,
      startingIndex,
      endingIndex
    );

    reorderPrimarySelections(newFilterOrder);
  };

  const { isOpen, onOpen, onClose } = useDisclosure();

  const selections = primaryTreeFilter?.primarySelections ?? [];

  const { getPlotColor } = useChartTheme();

  const getChipColor = (idx: number) => {
    const primaryFilterTagColors = primaryFilter.tagColors;
    if (
      (!primaryFilterTagColors && !areFilterChipsColored) ||
      !primaryTreeFilter?.primarySelections
    ) {
      return undefined;
    }
    return getPlotColor(idx);
  };

  const {
    containerRef: scrollContainerRef,
    showLeft,
    showRight,
    scrollToEdge,
  } = useScrollButtons();

  // Derived synchronously from `loggedInUser` rather than read off
  // `useUserTrialDetails`, which only publishes `isSelfSignupUser` from an
  // effect: until that lands the flag is `undefined`, and it now decides which
  // companies the picker offers and whether a submit is honoured, not just
  // whether a button is greyed out.
  const { loggedInUser } = useGetLoggedInUser();
  const isSelfSignupUser = isSelfSignupUserAccount(loggedInUser);

  // when opening a primary filter, it should focus on search input
  const popoverFocusRef = useRef<HTMLInputElement>(null);

  // While a saved view is previewed, its entities stand in for ours.
  const previewPrimary = useSavedViewPreview()?.lines.find(
    (line) => line.kind === 'primary'
  );
  // The live strip scrolls sideways; the preview wraps instead, clear of the
  // saved-sets panel, so every entity of the set can be read at once.
  const previewStrip = previewPrimary?.values.map((name, index) => (
    <FilterTag
      key={`${index}-${name}`}
      className={previewStyles.previewChip}
      data-active
      data-testid="saved-view-preview-primary-chip"
    >
      {name}
    </FilterTag>
  ));

  const chipStrip = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={selections.map((filter) => filter.id)}
        strategy={horizontalListSortingStrategy}
      >
        {selections.map((filter, idx) => (
          <PrimaryFilterTag
            key={filter.id}
            id={filter.id}
            label={filter.name}
            color={getChipColor(idx)}
            onRemoveChip={(event) => {
              event.stopPropagation();
              removePrimaryFilter(filter.id);
            }}
            isRemoveDisabled={selections.length <= 1}
          />
        ))}
      </SortableContext>
    </DndContext>
  );

  return (
    <div className={overflowStyles.column}>
      <div className={overflowStyles.row}>
        <div
          className={classNames(
            TourClasses.TOUR_TRACKING_CLASS,
            overflowStyles.scrollRow
          )}
          data-testid="filter-chips-toggle-column"
        >
          <div
            className={classNames(
              overflowStyles.chipStripAnchor,
              previewStrip && previewStyles.besidePanel
            )}
          >
            <div
              ref={scrollContainerRef}
              className={classNames(
                overflowStyles.chipStrip,
                previewStrip && overflowStyles.chipStripWrap
              )}
            >
              {previewStrip ?? chipStrip}
            </div>

            <ScrollButton
              direction="left"
              visible={showLeft}
              onClick={() => scrollToEdge('left')}
            />
            <ScrollButton
              direction="right"
              visible={showRight}
              onClick={() => scrollToEdge('right')}
            />
          </div>

          <div className={overflowStyles.addSlot}>
            <Popover
              placement="bottom-start"
              open={isOpen}
              onOpenChange={(next) => (next ? onOpen() : onClose())}
              onOpenAutoFocus={focusOnOpen(popoverFocusRef)}
              onInteractOutside={keepOpenForPortaledChildren}
              className={addStyles.popover}
              data-testid="filter-popover-content-open"
              trigger={
                <IconButton
                  aria-label="Add entity"
                  icon={
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 5.625V10M10 10V14.375M10 10H5.625M10 10H14.375"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  }
                  variant="ghost"
                  size="md"
                  shape="squared"
                  className={addStyles.addButton}
                  data-testid="primary-filter-add-button"
                />
              }
            >
              {primaryTreeFilter && (
                <PrimaryFilterFormRender
                  close={onClose}
                  selectedFilterId={primaryFilter.id}
                  primaryFilter={primaryTreeFilter}
                  isOpen={isOpen}
                  searchInputRef={popoverFocusRef}
                  isSelfSignupUser={isSelfSignupUser}
                  disallowMultiLevelSelect={false}
                />
              )}
            </Popover>
          </div>
        </div>
      </div>
    </div>
  );
};

const getAreFilterChipsColored = (items: PositionedItem[]) => {
  return items.some((item) => {
    const chartType = item.plot?.chartType;
    return (
      chartType === 'box' ||
      chartType === 'distribution' ||
      chartType === 'line'
    );
  });
};
